import React from 'react'
import {
  FormControl,
  Radio,
  RadioGroup,
  styled,
  alpha,
  FormControlLabel,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Control, Controller, FieldValues } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { getCemFeatures, getUserInfo } from 'src/store/userInfo/userInfoSlice'
import { isFeatureFlagEnabled } from 'src/rsmCoreComponents/utils/featureFlagUtils'
import { tokens } from '../../../../styles/materialTheme'
import { Styles } from '../../../../types'

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  '.MuiFormControlLabel-label': {
    fontFamily: 'Prelo-Book',
    color: theme.palette.text.primary,
  },
}))

const styles: Styles = {
  bpmb32: (theme) => ({
    [theme.breakpoints.down('tablet')]: {
      width: '100%',
    },
    [theme.breakpoints.up('mobile')]: {
      width: '50%',
    },
    [theme.breakpoints.only('tablet')]: {},
  }),
  paddingContainer: (theme) => ({
    [theme.breakpoints.only('tablet')]: {
      width: '100%',
    },
    [theme.breakpoints.only('mobile')]: {
      width: '100%',
    },
    marginBottom: '2.5rem',
  }),
  bppr5mb9: (theme) => ({
    [theme.breakpoints.down('tablet')]: {
      width: '100%',
    },
    [theme.breakpoints.up('mobile')]: {
      width: '25%',
    },
    marginBottom: '2.5rem',
  }),
  bppr10mb8: (theme) => ({
    [theme.breakpoints.down('tablet')]: {
      width: '100%',
      marginBottom: '2.5rem',
    },
    [theme.breakpoints.up('mobile')]: {
      width: '50%',
    },
    [theme.breakpoints.up('lg')]: {
      paddingRight: '1.625rem',
    },
  }),
  error: {
    display: 'inline',
    float: 'left',
    fontFamily: 'Prelo-Book, sans-serif',
    fontSize: '.875rem',
    fontWeight: 400,
    color: tokens.colors.rsmRed.secondary,
    paddingTop: '0rem',
  },
}

interface FormCardProps {
  control: Control<FieldValues, any>
  handleRadioChange: (value: string) => void
  contactMethod: string
}

const FormPreferredContactRadio = ({
  control,
  handleRadioChange,
  contactMethod,
}: FormCardProps) => {
  const { t } = useTranslation()
  const userInfo = useSelector(getUserInfo)
  const cemFeatures = useSelector(getCemFeatures)
  const isNewSupportEnabled = isFeatureFlagEnabled(
    'Support',
    userInfo.emailAddress,
    cemFeatures,
  )

  return (
    <FormControl
      style={isNewSupportEnabled ? undefined : { padding: '0 1.4375rem' }}
      component="fieldset"
      sx={styles.paddingContainer}>
      <FormControl
        component="legend"
        sx={(theme) => ({
          fontFamily: 'Prelo-Black, sans-serif',
          fontSize: '1rem',
          paddingBottom: '0.5rem',
          color: theme.palette.text.primary,
          display: 'block',
        })}>
        <span data-testid="Lbl_Support_ContactMethodLabel">
          {t('ContactMethod')}&nbsp;*
        </span>
      </FormControl>

      <Controller
        name="preferredContactMethod"
        aria-labelledby="contact-method-label"
        control={control}
        defaultValue={contactMethod} // Ensure correct default
        render={({ field }) => (
          <RadioGroup
            row
            name={field.name} // Manually set name
            value={field.value} // Sync value with react-hook-form
            onChange={(e) => {
              field.onChange(e.target.value) // Notify react-hook-form
              handleRadioChange(e.target.value) // Notify parent component
            }}>
            <StyledFormControlLabel
              value="Email"
              sx={{
                paddingBottom: '0rem',
                paddingTop: '0rem',
                paddingRight: '3rem',
                borderColor: alpha('#991B1B', 1),
              }}
              control={
                <Radio
                  id="preferredContactMethodEmail"
                  aria-describedby="contact-method-label"
                />
              }
              label="Email"
            />
            <StyledFormControlLabel
              value="Phone"
              control={<Radio id="preferredContactMethodPhone" />}
              label="Phone"
            />
          </RadioGroup>
        )}
      />
    </FormControl>
  )
}

export default FormPreferredContactRadio
